import { useEffect, useState } from "react";

import { format } from "date-fns";
import { generatePath, Link } from "react-router-dom";

import {
  CHANNEL_DETAIL_INCLUDE,
  getChannelDetailData,
} from "@/api/endpoints/channels";
import YesIcon from "@/assets/icons/check-badge.svg";
import NoIcon from "@/assets/icons/no-symbol.svg";
import Cover from "@/containers/Episode/partials/Cover";
import { INTERNAL_ROUTES } from "@/routes_new";
import { Channel } from "@/types/Channel";
import { truncate } from "@/utils/truncate";
import SpinnerAnimated from "@components/SpinnerAnimated";
import Table from "@components/Table";

import StatusLegend, { StatusColor } from "../../StatusLegend";
import { colorByTranscriptionStatus } from "../../utils";
import OrderingButton from "../OrderingButton";

import styles from "./ChannelListTable.module.css";

const ChannelRow: React.FunctionComponent<{ channel: Channel }> = ({
  channel,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [channelDetails, setChannelDetails] = useState<Channel>();

  useEffect(() => {
    setLoading(true);

    getChannelDetailData(channel.id, [
      CHANNEL_DETAIL_INCLUDE.EPISODE_COUNT,
      CHANNEL_DETAIL_INCLUDE.LATEST_PUBLISHED_DATE,
      CHANNEL_DETAIL_INCLUDE.TRANSCRIPTION_STATUS,
      CHANNEL_DETAIL_INCLUDE.AUTOMATIC_TRANSCRIPTION,
    ])
      .then((response) => {
        if (response.ok && response.data) {
          setChannelDetails(response.data);
        } else {
          throw new Error(response.statusText);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [channel.id]);

  return (
    <tr>
      <td>
        <Cover
          className={styles.cover}
          dumb
          episode={{
            source: channel.channel_type,
            cover: channel.artwork_url,
          }}
        />
      </td>
      <td className={styles.channelNameColumn}>
        <Link
          to={generatePath(INTERNAL_ROUTES.CHANNEL_DETAIL, {
            id: channel.id,
          })}
        >
          {channel.name}
        </Link>
        <p className={styles.channelDescription}>
          {truncate(channel.description, 150)}
        </p>
      </td>
      <td>{channel.refresh_module}</td>
      <td>{channel.channel_type}</td>
      <td>
        {loading ? (
          <SpinnerAnimated fill="currentColor" width="1.2rem" />
        ) : (
          channelDetails?.episode_count && channelDetails.episode_count
        )}
      </td>
      <td>{channel.current_reach}</td>
      <td>
        {loading && !channelDetails?.latest_published_date ? (
          <SpinnerAnimated fill="currentColor" width="1.2rem" />
        ) : channelDetails?.latest_published_date ? (
          format(new Date(channelDetails.latest_published_date), "PP HH:mm")
        ) : null}
      </td>
      <td>
        {loading ? (
          <SpinnerAnimated fill="currentColor" width="1.2rem" />
        ) : (
          <StatusLegend
            color={
              colorByTranscriptionStatus[
                channelDetails?.transcription_status || "no_transcript"
              ] as StatusColor
            }
            size="large"
          />
        )}
      </td>
      <td>
        {loading ? (
          <SpinnerAnimated fill="currentColor" width="1.2rem" />
        ) : (
          <div className={styles.transcriptionMode}>
            {channelDetails?.automatic_transcription ? (
              <>
                <YesIcon
                  fill="currentColor"
                  width="1.0rem"
                  className={styles.transcriptionModeIcon}
                />
                Yes
              </>
            ) : (
              <>
                <NoIcon
                  stroke="currentColor"
                  width="1.0rem"
                  className={styles.transcriptionModeIcon}
                />
                No
              </>
            )}
          </div>
        )}
      </td>
    </tr>
  );
};

const ChannelListTable: React.FunctionComponent<{ channels: Channel[] }> = ({
  channels,
}) => {
  return (
    <>
      <Table>
        <thead>
          <tr>
            <OrderingButton label="Channel" id="name" />
            <th></th>
            <th>Module</th>
            <th>Type</th>
            <th>Episodes</th>
            <th>Reach</th>
            <th>Latest episode</th>
            <th>Status</th>
            <th>Transcribed automatically</th>
          </tr>
        </thead>
        <tbody>
          {channels.map((channel) => (
            <ChannelRow channel={channel} key={channel.id} />
          ))}
        </tbody>
      </Table>
    </>
  );
};

export default ChannelListTable;
